'use client';

import { useFrame, useLoader, useThree } from '@react-three/fiber';
import React, { useEffect, useMemo, useRef } from 'react';
import { type CompanionActivity, createBerryMotion } from './motion';
import { OrthographicCamera } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

let lastVisiblePose:
  | {
      snapshot: ReturnType<ReturnType<typeof createBerryMotion>['snapshot']>;
      at: number;
    }
  | undefined;

export function BerryModel({
  activity,
  onReady,
}: {
  activity: CompanionActivity;
  onReady: () => void;
}) {
  const gltf = useLoader(GLTFLoader, '/models/berry.glb');
  const scene = useMemo(() => gltf.scene.clone(true), [gltf]);
  const motion = useMemo(
    () => createBerryMotion(scene, gltf.animations),
    [scene, gltf.animations],
  );
  const prepared = useRef(false);
  const wasAnimating = useRef(false);
  const invalidate = useThree((state) => state.invalidate);
  const current = useRef(activity);
  current.current = activity;
  const ready = useRef(false);

  useFrame(({ camera, size }, delta) => {
    const input = current.current;
    if (!prepared.current || (input.animate && !wasAnimating.current)) {
      if (
        input.animate &&
        lastVisiblePose &&
        Date.now() - lastVisiblePose.at < 1500
      ) {
        motion.restore(lastVisiblePose.snapshot);
      } else if (!prepared.current) {
        motion.advance(0, {
          ...input,
          greeting: 0,
          animate: false,
          state:
            input.animate &&
            (input.state === 'thinking' || input.state === 'responding')
              ? 'attentive'
              : input.state,
        });
      }
      prepared.current = true;
    }
    wasAnimating.current = input.animate;
    const framing = motion.advance(delta, input);
    if (input.animate) {
      lastVisiblePose = { snapshot: motion.snapshot(), at: Date.now() };
    }
    camera.position.set(framing.targetX + 0.22, framing.targetY + 1.1, 8);
    camera.lookAt(framing.targetX, framing.targetY, 0);
    if (camera instanceof OrthographicCamera) {
      camera.zoom = size.height / framing.height;
      camera.updateProjectionMatrix();
    }
    if (!ready.current) {
      ready.current = true;
      onReady();
    }
  });

  useEffect(() => {
    invalidate();
    if (!activity.animate) return;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      invalidate();
      timer = setTimeout(tick, motion.frameInterval());
    };
    timer = setTimeout(tick, motion.frameInterval());
    return () => clearTimeout(timer);
  }, [
    activity.animate,
    activity.state,
    activity.greeting,
    activity.lookYaw,
    activity.lookPitch,
    invalidate,
    motion,
  ]);

  return (
    <primitive
      object={scene}
      dispose={null}
    />
  );
}
